import isNaN from 'lodash/isNaN';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import { transformPhoneToMasked } from '@direct-frontend/components';
import { type Primitive } from '@direct-frontend/stdlib';
import { EMPTY_DASH } from '../../constants';
import { formatDate } from './formatDate';
import { formatPrice } from './formatPrice';

export interface FormatBaseOptions<I = string> {
    invalidValue?: I;
    locale?: string;
    timeZone?: Intl.DateTimeFormatOptions['timeZone'];
}

interface TextOptions<I = string> extends FormatBaseOptions<I> {
    type?: 'text';
}

interface PriceOptions<I = string> extends FormatBaseOptions<I> {
    type: 'price';
    code?: string;
    noFraction?: boolean;
}

interface DateOptions<I = string> extends FormatBaseOptions<I> {
    type: 'date';
    format?: Intl.DateTimeFormatOptions;
}

interface PrettyDateOptions<I = string> extends FormatBaseOptions<I> {
    withTime?: boolean;
}

interface PhoneOptions<I = string> extends FormatBaseOptions<I> {
    type: 'phone';
}

interface NumberOptions<I = string> extends FormatBaseOptions<I> {
    type: 'number';
    format?: Intl.NumberFormatOptions;
}

export type FormatOptions<I = string> =
    | TextOptions<I>
    | PriceOptions<I>
    | DateOptions<I>
    | PhoneOptions<I>
    | NumberOptions<I>;

export type FormatPreset =
    // 100,45 ₽
    | 'price'
    // 100 ₽ (без копеек)
    | 'price-integer'
    | 'percent'
    // 100 ₽/шт
    | 'price-per-part'
    | 'date'
    | 'iso-date'
    | 'time'
    | 'datetime'
    | 'datetime-short'
    | 'date-string'
    | 'pretty-datetime'
    | 'pretty-date'
    | 'phone'
    | 'kg'
    | 'gram'
    | 'parts';

/*
 * Функция форматирования строки по заданным пресетам или с готовыми опциями
 * @param value
 * @param presetOrOptions
 * @param baseOptions
 */
export function format<I = string>(
    value: Primitive,
    presetOrOptions?: FormatOptions<I> | FormatPreset,
    baseOptions?: FormatBaseOptions<I>
): string | I {
    let invalidValue: I | string = EMPTY_DASH;

    if (baseOptions && typeof baseOptions?.invalidValue !== 'undefined') {
        invalidValue = baseOptions.invalidValue;
    } else if (typeof presetOrOptions === 'object' && typeof presetOrOptions?.invalidValue !== 'undefined') {
        invalidValue = presetOrOptions.invalidValue;
    }

    if (typeof value === 'undefined' || value === null) {
        return invalidValue;
    }

    if (typeof value === 'symbol') {
        value = value.toString();
    }

    if (typeof value === 'string' && !value) {
        return invalidValue;
    }

    if (typeof presetOrOptions === 'string') {
        return formatPreset(value, presetOrOptions, baseOptions);
    }

    return formatOptions(value, presetOrOptions);
}

function formatPreset<I>(
    value: string | number | boolean | bigint,
    preset: FormatPreset,
    options?: FormatBaseOptions<I>
): string | I {
    const invalidValue = options && typeof options.invalidValue !== 'undefined' ? options.invalidValue : EMPTY_DASH;

    switch (preset) {
        case 'percent':
            if (typeof value !== 'number' || value < 0) {
                return invalidValue;
            }

            return formatOptions(value, {
                ...options,
                type: 'number',
                format: {
                    style: 'unit',
                    unit: 'percent',
                },
            });
        case 'price':
            return formatOptions(toString(value), { ...options, type: 'price', code: 'RUB' });
        case 'price-integer':
            return formatOptions(toString(value), { ...options, type: 'price', code: 'RUB', noFraction: true });
        case 'price-per-part':
            const price = formatOptions(toString(value), {
                ...options,
                invalidValue: null,
                type: 'price',
                code: 'RUB',
            });

            return price === null ? invalidValue : `${price}/шт`;
        case 'date':
            return formatOptions(value, {
                ...options,
                type: 'date',
                format: {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                },
            });
        case 'iso-date':
            return formatOptions(value, {
                ...options,
                locale: 'en-CA',
                type: 'date',
                format: {
                    timeZone: options?.timeZone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                },
            });
        case 'time':
            return formatOptions(value, {
                ...options,
                type: 'date',
                format: {
                    hour: '2-digit',
                    minute: '2-digit',
                },
            });
        case 'datetime':
            return formatOptions(value, {
                ...options,
                type: 'date',
                format: {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                },
            });
        case 'datetime-short':
            return formatOptions(value, {
                ...options,
                type: 'date',
                format: {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                },
            });
        case 'date-string':
            return formatOptions(value, {
                ...options,
                type: 'date',
                format: {
                    month: 'long',
                    day: '2-digit',
                },
            });
        case 'pretty-datetime':
            return formatPrettyDate(value, { invalidValue, withTime: true, ...options });
        case 'pretty-date':
            return formatPrettyDate(value, { invalidValue, withTime: false, ...options });
        case 'phone':
            return formatOptions(value, {
                ...options,
                type: 'phone',
            });
        case 'kg':
            return formatOptions(value, {
                ...options,
                type: 'number',
                format: {
                    style: 'unit',
                    unit: 'kilogram',
                },
            });
        case 'gram':
            return formatOptions(value, {
                ...options,
                type: 'number',
                format: {
                    style: 'unit',
                    unit: 'gram',
                },
            });
        case 'parts':
            const num = formatOptions(value, {
                ...options,
                invalidValue: null,
                type: 'number',
            });

            return num === null ? invalidValue : `${num} шт`;

        default:
            return formatOptions(value, options);
    }
}

function formatPrettyDate<I>(value: string | number | boolean | bigint, options: PrettyDateOptions<I>) {
    const invalidValue = options && typeof options.invalidValue !== 'undefined' ? options.invalidValue : EMPTY_DASH;

    if (typeof value === 'bigint' || typeof value === 'boolean') {
        return invalidValue;
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
        return invalidValue;
    }

    const withTime = options.withTime;

    const withTimeSuffix = withTime ? `, ${formatPreset(value, 'time')}` : '';

    const currentDate = new Date();

    const isToday =
        date.getFullYear() === currentDate.getFullYear() &&
        date.getMonth() === currentDate.getMonth() &&
        date.getDate() === currentDate.getDate();

    if (isToday) {
        return `сегодня${withTimeSuffix}`;
    }

    const yesterdayDate = new Date(currentDate);

    yesterdayDate.setDate(currentDate.getDate() - 1);

    const isYesterday =
        date.getFullYear() === yesterdayDate.getFullYear() &&
        date.getMonth() === yesterdayDate.getMonth() &&
        date.getDate() === yesterdayDate.getDate();

    if (isYesterday) {
        return `вчера${withTimeSuffix}`;
    }

    const tomorrowDate = new Date(currentDate);

    tomorrowDate.setDate(currentDate.getDate() + 1);

    const isTomorrow =
        date.getFullYear() === tomorrowDate.getFullYear() &&
        date.getMonth() === tomorrowDate.getMonth() &&
        date.getDate() === tomorrowDate.getDate();

    if (isTomorrow) {
        return `завтра${withTimeSuffix}`;
    }

    const isCurrentYear = date.getFullYear() === currentDate.getFullYear();

    if (!isCurrentYear) {
        return formatOptions(value, {
            ...options,
            type: 'date',
            format: {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                ...(withTime
                    ? {
                          hour: '2-digit',
                          minute: '2-digit',
                      }
                    : {}),
            },
        });
    }

    return formatOptions(value, {
        ...options,
        type: 'date',
        format: {
            month: 'long',
            day: 'numeric',
            ...(withTime
                ? {
                      hour: '2-digit',
                      minute: '2-digit',
                  }
                : {}),
        },
    });
}

function formatOptions<I>(value: string | number | boolean | bigint, options?: FormatOptions<I>): string | I {
    const invalidValue = options && typeof options.invalidValue !== 'undefined' ? options.invalidValue : EMPTY_DASH;
    const locale = options?.locale ?? 'ru';

    switch (options?.type) {
        case 'price':
            return formatPrice(toString(value), { code: options.code, invalidValue, noFraction: options.noFraction });
        case 'date':
            if (typeof value === 'bigint' || typeof value === 'boolean') {
                return invalidValue;
            }

            return formatDate(value, { format: options.format, locale, invalidValue });
        case 'phone':
            let strValue = toString(value).trim();
            const trimValue = toString(strValue).replace(/\D/g, '');

            if (trimValue.length === 11 && (trimValue.startsWith('8') || trimValue.startsWith('7'))) {
                strValue = strValue.slice(1);

                return transformPhoneToMasked(strValue);
            }

            if (trimValue.length === 10) {
                return transformPhoneToMasked(strValue);
            }

            return strValue || EMPTY_DASH;
        case 'number':
            const numberFormat = new Intl.NumberFormat(locale, options.format);

            return numberFormat.format(toNumber(value));

        default:
            return toString(value) || invalidValue;
    }
}
